import type {ReactElement} from 'react';
import React from 'react';
import {useDownloadPanelData} from '@Components/download-panel/download-panel.context';
import {Text, TextSize} from '@Components/text';
import styles from './download-video-duration-information.module.scss';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {FileTypeOptions} from '@Components/download-panel/components/file-type-option/file-type-option.types';
import {isPosterVideo} from '@Libraries/poster-object.library';
import {DOWNLOAD_MAX_DURATION} from '@Libraries/poster-download-library';

export function DownloadVideoDurationInformation(): ReactElement | null {
  const {speed, posterObject, fileType, duration, setDuration, setSpeed, originalDuration} = useDownloadPanelData();

  if (!isPosterVideo(posterObject)) {
    return null;
  }

  function getMinSecLoopsRemainingText(remainingTime: number, loopCount: number, times: string): string {
    const minutes = Math.floor(remainingTime / 60);
    const seconds = parseFloat((remainingTime % 60).toFixed(1));
    const epsilon = 0.01;

    if (seconds < epsilon) {
      return minutes === 1
        ? window.i18next.t('pmwjs_loops_x_times_and_1_minute_remaining', {amount: loopCount, times})
        : window.i18next.t('pmwjs_loops_x_times_and_y_minutes_remaining', {amount: loopCount, times, minutes});
    }

    if (Math.abs(seconds - 1) < epsilon) {
      return minutes === 1
        ? window.i18next.t('pmwjs_loops_x_times_and_1_minute_and_1_second_remaining', {amount: loopCount, times})
        : window.i18next.t('pmwjs_loops_x_times_and_y_minutes_and_1_second_remaining', {amount: loopCount, times, minutes});
    }

    return minutes === 1
      ? window.i18next.t('pmwjs_loops_x_times_and_1_minute_and_y_seconds_remaining', {amount: loopCount, times, seconds})
      : window.i18next.t('pmwjs_loops_x_times_and_y_minutes_and_z_seconds_remaining', {amount: loopCount, times, minutes, seconds});
  }

  function getMinSecTrimText(trimmedTime: number): string {
    const minutes = Math.floor(trimmedTime / 60);
    const seconds = parseFloat((trimmedTime % 60).toFixed(1));
    const epsilon = 0.01;

    if (seconds < epsilon) {
      return minutes === 1 ? window.i18next.t('pmwjs_trims_1_minute_of_content') : window.i18next.t('pmwjs_trims_x_minutes_of_content', {minutes});
    }

    if (Math.abs(seconds - 1) < epsilon) {
      return minutes === 1 ? window.i18next.t('pmwjs_trims_1_minute_and_1_second_of_content') : window.i18next.t('pmwjs_trims_x_minutes_and_1_second_of_content', {minutes});
    }

    return minutes === 1
      ? window.i18next.t('pmwjs_trims_1_minute_and_y_seconds_of_content', {seconds})
      : window.i18next.t('pmwjs_trims_x_minutes_and_y_seconds_of_content', {minutes, seconds});
  }

  function getDownloadOptionText(originalDuration: number, duration: number, speed: number): {icon: string; text: string} | null {
    const contentNeeded = duration * speed;
    const epsilon = 0.01;

    if (Math.abs(contentNeeded - originalDuration) < epsilon) {
      if (speed === 1) {
        return null;
      }
      if (speed < 1) {
        return {icon: 'icon-slow-speed', text: window.i18next.t('pmwjs_plays_once_slowly')};
      }
      if (speed > 1) {
        return {icon: 'icon-fast-speed', text: window.i18next.t('pmwjs_plays_once_faster')};
      }
    }

    if (contentNeeded > originalDuration) {
      const loopCount = Math.floor(contentNeeded / originalDuration);
      const remainder = contentNeeded % originalDuration;

      const times = loopCount === 1 ? 'time' : 'times';
      const remainingTime = parseFloat(remainder.toFixed(1));

      if (Math.abs(remainingTime) < epsilon) {
        return {
          icon: 'icon-repeat',
          text: window.i18next.t('pmwjs_loops_x_times', {
            amount: loopCount,
            times: times,
          }),
        };
      }

      if (remainingTime === 1) {
        return {
          icon: 'icon-repeat',
          text: window.i18next.t('pmwjs_loops_x_times_with_one_remaining_second', {
            amount: loopCount,
            times: times,
          }),
        };
      }

      if (remainingTime >= 60) {
        return {
          icon: 'icon-repeat',
          text: getMinSecLoopsRemainingText(remainingTime, loopCount, times),
        };
      }

      return {
        icon: 'icon-repeat',
        text: window.i18next.t('pmwjs_loops_x_times_and_y_remaining_seconds', {
          amount: loopCount,
          times: times,
          remainingSeconds: remainingTime,
        }),
      };
    }

    if (contentNeeded < originalDuration) {
      const trimmedTime = parseFloat((originalDuration - contentNeeded).toFixed(1));
      if (trimmedTime === 1) {
        return {
          icon: 'icon-cut',
          text: window.i18next.t('pmwjs_trims_1_second_of_content'),
        };
      }

      if (trimmedTime >= 60) {
        return {
          icon: 'icon-cut',
          text: getMinSecTrimText(trimmedTime),
        };
      }

      return {
        icon: 'icon-cut',
        text: window.i18next.t('pmwjs_trims_y_seconds_of_content', {
          time: trimmedTime,
        }),
      };
    }

    return null;
  }

  const onReset = (): void => {
    setDuration(Math.min(originalDuration, DOWNLOAD_MAX_DURATION));
    setSpeed(1);
  };

  const getDurationInformation = (): ReactElement | null => {
    const isMp4DurationCapped = fileType === FileTypeOptions.MP4 && originalDuration > DOWNLOAD_MAX_DURATION;
    const details = getDownloadOptionText(originalDuration, duration, speed);
    if (details) {
      const text = isMp4DurationCapped ? `${window.i18next.t('pmwjs_duration_limited_to_10_mins')} ${details.text}` : details.text;
      return (
        <div className={`_full-width radius-8 ${styles.container} spacing-p-r-2 spacing-p-l-2 spacing-p-t-1 spacing-p-b-1`}>
          <Icon size={IconSize.SIZE_ICON_16} className={`content-primary spacing-m-r-1 ${styles.inlineIcon}`} icon={details.icon} />
          <Text dangerouslySetInnerHTML className="content-primary spacing-m-r-1" size={TextSize.XXSMALL} bold val={text} />
          {!(fileType === FileTypeOptions.GIF && originalDuration > 20) && !(isMp4DurationCapped && duration === DOWNLOAD_MAX_DURATION) ? (
            <Text onClick={onReset} bold className="content-primary _cursor-pointer" underline size={TextSize.XXSMALL} val={window.i18next.t('pmwjs_reset')} />
          ) : null}
        </div>
      );
    }
    return null;
  };

  return getDurationInformation();
}
